import { ColumnType, DataCell, TableColumn } from '@ecosystem/export-api';
import { ExcelFormatter, Fact, formatKpiForExport, Kpi } from '@ecosystem/formatters';
import {
  ColumnHeaderDisplayNameForPA,
  ColumnHeaderForPA,
  PerformanceAnalysisboardPg,
} from '@models/PerformanceAnalysisboard';
import { getPerformanceAnalysisboardColumnsForExcelExport } from '@components/performance-analysis-board/performance-analysis-table-columns/PerformanceAnalysisTableColumns';
import { FactRevenue, Filters, ShareType } from '@utils/pageConfig/Types';
import { ColumnConfig } from '@components/design/migrated/Table';
import { formatKpi, ValueType } from '@utils/Formatters';
import { revenueShareType } from '@utils/hook/tableCellConst/TableCellConst';
import { CategoryLeaderboardPg } from '@models/CategoryLeaderboard';
import { getOpportunityReviewTableColumns } from '@components/opportunity-review-board/opportunity-review-table-columns/OpportunityReviewTableColumns';
import {
  ColumnHeaderDisplayNameForOR as ColumnHeaderDisplayNameOR,
  ColumnHeaderForOR as ColumnHeaderOR,
  KpiNoGrowth,
  OpportunityReviewTableContent,
  SaleType,
  SwotCategory,
} from '@models/OpportunityReview';
import {
  ColumnHeader as ColumnHeaderProducts,
  ColumnHeaderDisplayName as ColumnHeaderDisplayNameProducts,
  Feature,
  LeaderboardKpi,
  ProductsCompareExport,
  Symbol,
} from '@models/Products';
import {
  constructCurrencyLabel,
  formatCurrencyLabelColumnConfig,
  formatLabelIntoRevenuePriceTitle,
} from '@utils/currencyUtils/CurrencyUtils';
import { FEATURE_UNAVAILABLE } from '@components/products-compare-modal/Types';
import { formatFirstActivity } from '@utils/PeriodUtils';
import {
  ColumnHeader as ColumnHeaderTA,
  ColumnHeaderDisplayName as ColumnHeaderDisplayNameTA,
} from '@models/TradingAssistant';
import { BrandDeepDiveChartRespDataLookup, BrandDependencyChartSubResp } from '@models/BrandDependency';

export const getTableExportColumn = (factRevenue: FactRevenue, volume: string, hasGroups: boolean): TableColumn[] => {
  const RETAILER_REVENUE_INDEX = hasGroups ? 14 : 13;
  const RETAILER_VOLUME_INDEX = hasGroups ? 24 : 23;
  const header = [{ header: 'Market' }, ...(hasGroups ? [{ header: 'Grouping' }] : [])];
  let KpiValuesExceptShare: ColumnConfig[] = getPerformanceAnalysisboardColumnsForExcelExport(
    ShareType.MarketShare,
    volume,
  ).filter((o) => {
    return ![
      ColumnHeaderForPA.yourRevenueShare,
      ColumnHeaderForPA.yourVolumeShare,
      ColumnHeaderForPA.marketRetailerRevenueShare,
      ColumnHeaderForPA.marketRetailerVolumeShare,
    ].includes(o.headerKey as ColumnHeaderForPA);
  });
  KpiValuesExceptShare.forEach((kpis) => {
    //Exclude currency symbol from Market Volume
    const headerWithLabel = formatCurrencyLabelColumnConfig(kpis, factRevenue);
    const currencyString =
      kpis.headerKey !== ColumnHeaderForPA.marketVolume &&
      kpis.headerKey !== ColumnHeaderForPA.yourVolume &&
      kpis.headerKey !== ColumnHeaderForPA.marketExcludeRetailVolume
        ? `(${factRevenue.currencySymbol})`
        : ``;
    header.push(
      {
        header: `${headerWithLabel} ${currencyString}`, //NOSONAR typescript:S6551
      },
      {
        header: `${headerWithLabel} Change ${currencyString}`, //NOSONAR typescript:S6551
      },
      {
        header: `${headerWithLabel} Change (%)`, //NOSONAR typescript:S6551
      },
    );
  });

  const currencyLabel = constructCurrencyLabel(factRevenue);

  const retailerRevenueShareMix = [
    {
      header: `${formatLabelIntoRevenuePriceTitle(
        ColumnHeaderDisplayNameForPA()[ColumnHeaderForPA.yourRevenueShare],
        currencyLabel,
      )} (%)`,
    },
    {
      header: `${formatLabelIntoRevenuePriceTitle(
        ColumnHeaderDisplayNameForPA()[ColumnHeaderForPA.yourRevenueShare],
        currencyLabel,
      )} (pp)`,
    },
    {
      header: `${formatLabelIntoRevenuePriceTitle(
        ColumnHeaderDisplayNameForPA()[ColumnHeaderForPA.yourRevenueMix],
        currencyLabel,
      )} (%)`,
    },
    {
      header: `${formatLabelIntoRevenuePriceTitle(
        ColumnHeaderDisplayNameForPA()[ColumnHeaderForPA.yourRevenueMix],
        currencyLabel,
      )} (pp)`,
    },
  ];
  const retailerVolumeShareMix = [
    {
      header: `${ColumnHeaderDisplayNameForPA(volume)[ColumnHeaderForPA.yourVolumeShare]} (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameForPA(volume)[ColumnHeaderForPA.yourVolumeShare]} (pp)`,
    },
    {
      header: `${ColumnHeaderDisplayNameForPA(volume)[ColumnHeaderForPA.yourVolumeMix]} (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameForPA(volume)[ColumnHeaderForPA.yourVolumeMix]} (pp)`,
    },
  ];

  //insert revenue share/mix columns after retailer revenue
  header.splice(
    RETAILER_REVENUE_INDEX,
    0,
    retailerRevenueShareMix[0],
    retailerRevenueShareMix[1],
    retailerRevenueShareMix[2],
    retailerRevenueShareMix[3],
  );
  //insert volume share/mix columns after retailer volume
  header.splice(
    RETAILER_VOLUME_INDEX,
    0,
    retailerVolumeShareMix[0],
    retailerVolumeShareMix[1],
    retailerVolumeShareMix[2],
    retailerVolumeShareMix[3],
  );
  return header;
};

//Function to return column headers for OR table export. The ordering is based on given AC, do not simply change it.
export const getORTableExportColumn = (factRevenue: FactRevenue, volume: string, hasGroups): TableColumn[] => {
  const RETAILER_VOLUME_CHANGE_PERCENTAGE_INDEX = 13;
  const header = [{ header: 'Market' }, ...(hasGroups ? [{ header: 'Grouping' }] : [])];
  const columns: ColumnConfig[] = [
    ...getOpportunityReviewTableColumns(SaleType.VALUE),
    ...getOpportunityReviewTableColumns(SaleType.VOLUME, volume),
  ];
  let KpiValuesExceptShare: ColumnConfig[] = columns
    .filter((o) => {
      return (
        o.headerKey === ColumnHeaderOR.marketRevenue ||
        o.headerKey === ColumnHeaderOR.marketVolume ||
        o.headerKey === ColumnHeaderOR.myRevenue ||
        o.headerKey === ColumnHeaderOR.myVolume
      );
    })
    .sort((a, b) => a.headerKey.localeCompare(b.headerKey));
  KpiValuesExceptShare.forEach((kpis) => {
    //Exclude currency symbol from Market Volume & Retailer Volume
    const currencyString =
      kpis.headerKey !== ColumnHeaderOR.marketVolume && kpis.headerKey !== ColumnHeaderOR.myVolume
        ? `(${factRevenue.currencySymbol})`
        : ``;
    const kpiWithLabel = formatCurrencyLabelColumnConfig(kpis, factRevenue);
    header.push(
      {
        header: `${kpiWithLabel} ${currencyString}`, //NOSONAR typescript:S6551
      },
      {
        header: `${kpiWithLabel} Change ${currencyString}`, //NOSONAR typescript:S6551
      },
      {
        header: `${kpiWithLabel} Change (%)`, //NOSONAR typescript:S6551
      },
    );
  });
  let retailerRevenueVolumeShare: ColumnConfig[] = columns.filter((o) => {
    return o.headerKey === ColumnHeaderOR.myRevenueShare || o.headerKey === ColumnHeaderOR.myVolumeShare;
  });
  const retailerRevenueVolumeShareColumns = retailerRevenueVolumeShare.flatMap((kpis) => [
    { header: `${formatCurrencyLabelColumnConfig(kpis, factRevenue)} (%)` }, //NOSONAR typescript:S6551
    { header: `${formatCurrencyLabelColumnConfig(kpis, factRevenue)} Change (pp)` }, //NOSONAR typescript:S6551
  ]);
  //insert revenue/volume share & share change columns after retailer volume columns
  header.splice(RETAILER_VOLUME_CHANGE_PERCENTAGE_INDEX, 0, ...retailerRevenueVolumeShareColumns);

  const currencyLabel = constructCurrencyLabel(factRevenue);
  const currencyLabelWithoutBracket = factRevenue.currencyLabel ? ` ${factRevenue.currencyLabel}` : '';

  const marketGrowthKpis = [
    {
      header: `${
        ColumnHeaderDisplayNameOR()[ColumnHeaderOR.overUnderGrowthRevenue]
      } (Revenue${currencyLabelWithoutBracket})`,
    },
    {
      header: `${
        ColumnHeaderDisplayNameOR()[ColumnHeaderOR.overUnderFairShareRevenue]
      } (Revenue${currencyLabelWithoutBracket})`,
    },
    {
      header: `${ColumnHeaderDisplayNameOR()[ColumnHeaderOR.overUnderGrowthVolume]} ${volume}`,
    },
    {
      header: `${ColumnHeaderDisplayNameOR()[ColumnHeaderOR.overUnderFairShareVolume]} ${volume}`,
    },
  ];
  header.push(marketGrowthKpis[0], marketGrowthKpis[1], marketGrowthKpis[2], marketGrowthKpis[3]);
  header.push(
    {
      header: `${formatLabelIntoRevenuePriceTitle(
        ColumnHeaderDisplayNameOR()[ColumnHeaderOR.growthOpportunityRevenue],
        currencyLabel,
      )} (${factRevenue.currencySymbol})`,
    },
    {
      header: `${formatLabelIntoRevenuePriceTitle(
        ColumnHeaderDisplayNameOR()[ColumnHeaderOR.fairShareOpportunityRevenue],
        currencyLabel,
      )} (${factRevenue.currencySymbol})`,
    },
    {
      header: `${ColumnHeaderDisplayNameOR(volume)[ColumnHeaderOR.growthOpportunityVolume]}`,
    },
    {
      header: `${ColumnHeaderDisplayNameOR(volume)[ColumnHeaderOR.fairShareOpportunityVolume]}`,
    },
    {
      header: `${
        ColumnHeaderDisplayNameOR()[ColumnHeaderOR.swotCategoryRevenue]
      } (Revenue${currencyLabelWithoutBracket})`,
    },
    {
      header: `${ColumnHeaderDisplayNameOR()[ColumnHeaderOR.swotCategoryVolume]} ${volume}`,
    },
  );
  return header;
};

const valueIndex = 0;
const formatter = new ExcelFormatter();

//This function takes KPI value as param & returns formatted object as with formatted KPI value & ColumnType
export const getFormattedPercentageColumn = (value: number) => {
  // null KPI value to be displayed as "--"
  if (!value) {
    return {
      value: '--',
      type: ColumnType.Text,
    };
  }

  return {
    value: formatKpiForExport(Kpi.revenueShare, { value: value } as Fact, formatter)[valueIndex],
    type: ColumnType.Percentage,
  };
};
//This function takes KPI value as param & returns formatted object as with formatted KPI value & ColumnType
export const getFormattedNumberColumn = (value: number) => {
  if (value || value === 0) {
    return {
      value: formatKpi(revenueShareType, value, {
        as: ValueType.ABSOLUTE_CHANGE,
      }),
      type: ColumnType.Number,
    };
  }

  return {
    value: '',
    type: ColumnType.Text,
  };
};

//This function take KPI value as param and return it as Percentage Point ColumnType object
export const getFormattedPercentagePointColumn = (value: number) => {
  return {
    value: value,
    type: ColumnType.PercentagePoint,
  };
};

//This function takes KPI value as param & returns formatted object as with formatted KPI value & ColumnType
const getFormattedOverallAssessment = (swotValue: SwotCategory) => {
  return {
    value: swotValue ? swotValue.value : '--',
    type: ColumnType.Text,
  };
};

//This function takes KPI value as param & returns formatted object as with formatted KPI value & ColumnType
const getFormattedGrowth = (growthValue: string) => {
  return {
    value: growthValue || '--',
    type: ColumnType.Text,
  };
};

const formatNullKpiNoGrowthWithDashes = (growthValue: KpiNoGrowth, columnType: ColumnType) => {
  if (!growthValue) {
    return {
      value: '--',
      type: ColumnType.Text,
    };
  }

  return {
    value: growthValue.value,
    type: columnType,
  };
};

export const getTableExportRow = (
  data: PerformanceAnalysisboardPg[] | CategoryLeaderboardPg[],
  hasGroups: boolean,
): DataCell[][] => {
  return data.map((kpiData) => {
    const segmentOrPg = kpiData.segmentOrPg;
    return [
      { value: segmentOrPg, type: ColumnType.Text },
      ...(hasGroups ? [{ value: kpiData.grouping, type: ColumnType.Text }] : []),
      { value: kpiData.marketRevenue.value, type: ColumnType.Currency },
      { value: kpiData.marketRevenue.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketRevenue.percentage),
      { value: kpiData.marketExcludeRetailRevenue.value, type: ColumnType.Currency },
      { value: kpiData.marketExcludeRetailRevenue.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketExcludeRetailRevenue.percentage),
      { value: kpiData.marketVolume.value, type: ColumnType.Number },
      { value: kpiData.marketVolume.actual, type: ColumnType.Number },
      getFormattedPercentageColumn(kpiData.marketVolume.percentage),
      { value: kpiData.yourRevenue.value, type: ColumnType.Currency },
      { value: kpiData.yourRevenue.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.yourRevenue.percentage),
      getFormattedPercentageColumn(kpiData.yourRevenueShare.sharePercentage),
      {
        value:
          kpiData.yourRevenueShare.growthPercentage || kpiData.yourRevenueShare.growthPercentage === 0
            ? formatKpi(revenueShareType, kpiData.yourRevenueShare.growthPercentage, {
                as: ValueType.ABSOLUTE_CHANGE,
              })
            : '',
        type: ColumnType.Text,
      },
      getFormattedPercentageColumn(kpiData.yourRevenueMix.sharePercentage),
      getFormattedNumberColumn(kpiData.yourRevenueMix.growthPercentage),
      { value: kpiData.marketExcludeRetailVolume.value, type: ColumnType.Number },
      { value: kpiData.marketExcludeRetailVolume.actual, type: ColumnType.Number },
      getFormattedPercentageColumn(kpiData.marketExcludeRetailVolume.percentage),
      { value: kpiData.yourVolume.value, type: ColumnType.Number },
      { value: kpiData.yourVolume.actual, type: ColumnType.Number },
      getFormattedPercentageColumn(kpiData.yourVolume.percentage),
      getFormattedPercentageColumn(kpiData.yourVolumeShare.sharePercentage),
      getFormattedNumberColumn(kpiData.yourVolumeShare.growthPercentage),
      getFormattedPercentageColumn(kpiData.yourVolumeMix.sharePercentage),
      getFormattedNumberColumn(kpiData.yourVolumeMix.growthPercentage),
      { value: kpiData.marketPrice.value, type: ColumnType.Currency },
      { value: kpiData.marketPrice.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketPrice.percentage),
      { value: kpiData.marketExcludeRetailPrice.value, type: ColumnType.Currency },
      { value: kpiData.marketExcludeRetailPrice.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketExcludeRetailPrice.percentage),
      { value: kpiData.yourPrice.value, type: ColumnType.Currency },
      { value: kpiData.yourPrice.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.yourPrice.percentage),
    ];
  });
};

const createTextColumn = (value: any): DataCell => ({
  value,
  type: ColumnType.Text,
});

const createCurrencyColumn = (value: number): DataCell => ({
  value,
  type: ColumnType.Currency,
});

const createNumberColumn = (value: number): DataCell => ({
  value,
  type: ColumnType.Number,
});

const createFormattedPercentageColumn = (percentage: number | null | undefined): DataCell =>
  getFormattedPercentageColumn(percentage ?? null);

const createGrowthColumn = (growth: number | null | undefined): DataCell =>
  createTextColumn(
    growth || growth === 0 ? formatKpi(revenueShareType, growth, { as: ValueType.ABSOLUTE_CHANGE }) : '',
  );

export const getTrendingAssistanceNonDeepDiveRows = (data: BrandDependencyChartSubResp[]): DataCell[][] => {
  return data.map((kpiData) => [
    createTextColumn(kpiData.brand),

    createFormattedPercentageColumn(kpiData.retailerWithinBrandRevenue.sharePercentage),
    createGrowthColumn(kpiData.retailerWithinBrandRevenue.growthPercentage),

    createFormattedPercentageColumn(kpiData.brandWithinRetailerRevenue.sharePercentage),
    createGrowthColumn(kpiData.brandWithinRetailerRevenue.growthPercentage),

    createCurrencyColumn(kpiData.marketRevenue.value),
    createCurrencyColumn(kpiData.marketRevenue.actual),
    createFormattedPercentageColumn(kpiData.marketRevenue.percentage),

    createFormattedPercentageColumn(kpiData.marketRevenueShare.sharePercentage),
    createGrowthColumn(kpiData.marketRevenueShare.growthPercentage),

    createCurrencyColumn(kpiData.retailerRevenue.value),
    createCurrencyColumn(kpiData.retailerRevenue.actual),
    createFormattedPercentageColumn(kpiData.retailerRevenue.percentage),

    createFormattedPercentageColumn(kpiData.retailerRevenueShare.sharePercentage),
    createGrowthColumn(kpiData.retailerRevenueShare.growthPercentage),

    createFormattedPercentageColumn(kpiData.retailerWithinBrandUnits.sharePercentage),
    createGrowthColumn(kpiData.retailerWithinBrandUnits.growthPercentage),

    createFormattedPercentageColumn(kpiData.brandWithinRetailerUnits.sharePercentage),
    createGrowthColumn(kpiData.brandWithinRetailerUnits.growthPercentage),

    createNumberColumn(kpiData.marketUnits.value),
    createNumberColumn(kpiData.marketUnits.actual),
    createFormattedPercentageColumn(kpiData.marketUnits.percentage),

    createFormattedPercentageColumn(kpiData.marketUnitsShare.sharePercentage),
    createGrowthColumn(kpiData.marketUnitsShare.growthPercentage),

    createNumberColumn(kpiData.retailerUnits.value),
    createNumberColumn(kpiData.retailerUnits.actual),
    createFormattedPercentageColumn(kpiData.retailerUnits.percentage),

    createFormattedPercentageColumn(kpiData.retailerUnitsShare.sharePercentage),
    createGrowthColumn(kpiData.retailerUnitsShare.growthPercentage),
  ]);
};

export const getTrendingAssistanceDeepDiveRows = (
  data: Map<string, BrandDeepDiveChartRespDataLookup>,
  sortedDeepDiveBrands: string[],
): DataCell[][] => {
  if (!(data instanceof Map)) return [];

  return sortedDeepDiveBrands.flatMap((brand) => {
    const brandData = data.get(brand);
    if (!brandData) return [];

    return brandData.data.map((entry) => [
      createTextColumn(brand),
      createTextColumn(entry.featureValue),

      createFormattedPercentageColumn(entry.retailerWithinBrandRevenue.sharePercentage),
      createGrowthColumn(entry.retailerWithinBrandRevenue.growthPercentage),

      createFormattedPercentageColumn(entry.brandWithinRetailerRevenue.sharePercentage),
      createGrowthColumn(entry.brandWithinRetailerRevenue.growthPercentage),

      createCurrencyColumn(entry.marketRevenue.value),
      createCurrencyColumn(entry.marketRevenue.actual),
      createFormattedPercentageColumn(entry.marketRevenue.percentage),

      createFormattedPercentageColumn(entry.marketRevenueShare.sharePercentage),
      createGrowthColumn(entry.marketRevenueShare.growthPercentage),

      createCurrencyColumn(entry.retailerRevenue.value),
      createCurrencyColumn(entry.retailerRevenue.actual),
      createFormattedPercentageColumn(entry.retailerRevenue.percentage),

      createFormattedPercentageColumn(entry.retailerRevenueShare.sharePercentage),
      createGrowthColumn(entry.retailerRevenueShare.growthPercentage),

      createFormattedPercentageColumn(entry.retailerWithinBrandUnits.sharePercentage),
      createGrowthColumn(entry.retailerWithinBrandUnits.growthPercentage),

      createFormattedPercentageColumn(entry.brandWithinRetailerUnits.sharePercentage),
      createGrowthColumn(entry.brandWithinRetailerUnits.growthPercentage),

      createNumberColumn(entry.marketUnits.value),
      createNumberColumn(entry.marketUnits.actual),
      createFormattedPercentageColumn(entry.marketUnits.percentage),

      createFormattedPercentageColumn(entry.marketUnitsShare.sharePercentage),
      createGrowthColumn(entry.marketUnitsShare.growthPercentage),

      createNumberColumn(entry.retailerUnits.value),
      createNumberColumn(entry.retailerUnits.actual),
      createFormattedPercentageColumn(entry.retailerUnits.percentage),

      createFormattedPercentageColumn(entry.retailerUnitsShare.sharePercentage),
      createGrowthColumn(entry.retailerUnitsShare.growthPercentage),
    ]);
  });
};

//Function to return row values for OR table export. The ordering is based on the header, do not simply change it.
export const getORTableExportRow = (data: OpportunityReviewTableContent[], hasGroups: boolean): DataCell[][] => {
  return data.map((kpiData) => {
    return [
      { value: kpiData.segment, type: ColumnType.Text },
      ...(hasGroups ? [{ value: kpiData.grouping, type: ColumnType.Text }] : []),
      { value: kpiData.marketRevenue.value, type: ColumnType.Currency },
      { value: kpiData.marketRevenue.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketRevenue.percentage),
      { value: kpiData.marketVolume.value, type: ColumnType.Number },
      { value: kpiData.marketVolume.actual, type: ColumnType.Number },
      getFormattedPercentageColumn(kpiData.marketVolume.percentage),
      { value: kpiData.myRevenue.value, type: ColumnType.Currency },
      { value: kpiData.myRevenue.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.myRevenue.percentage),
      { value: kpiData.myVolume.value, type: ColumnType.Number },
      { value: kpiData.myVolume.actual, type: ColumnType.Number },
      getFormattedPercentageColumn(kpiData.myVolume.percentage),
      getFormattedPercentageColumn(kpiData.myShareRevenue.sharePercentage),
      {
        value: formatKpi(revenueShareType, kpiData.myShareRevenue.growthPercentage ?? 0, {
          as: ValueType.ABSOLUTE_CHANGE,
        }),
        type: ColumnType.Text,
      },
      getFormattedPercentageColumn(kpiData.myShareVolume.sharePercentage),
      getFormattedNumberColumn(kpiData.myShareVolume.growthPercentage),
      getFormattedGrowth(kpiData.growthPerformanceRevenue),
      getFormattedGrowth(kpiData.fairSharePerformanceRevenue),
      getFormattedGrowth(kpiData.growthPerformanceVolume),
      getFormattedGrowth(kpiData.fairSharePerformanceVolume),
      { value: kpiData.growthOppRevenue.value, type: ColumnType.Currency },
      formatNullKpiNoGrowthWithDashes(kpiData.fairShareOppRevenue, ColumnType.Currency),
      { value: kpiData.growthOppVolume.value, type: ColumnType.Number },
      formatNullKpiNoGrowthWithDashes(kpiData.fairShareOppVolume, ColumnType.Number),
      getFormattedOverallAssessment(kpiData.swotCategoryRevenue),
      getFormattedOverallAssessment(kpiData.swotCategoryVolume),
    ];
  });
};
export const isAnyFeatureExist = (data: LeaderboardKpi[]) => {
  return data.some((obj) => (obj.features ? obj.features.length > 0 : false));
};
export const getTopSellingExportColumn = (
  currency: string,
  data: LeaderboardKpi[],
  prodLeaderboardFeatureProductFilters: Filters[],
  hasGroups,
): TableColumn[] => {
  const dataColumn = getDataExportColumn(currency, hasGroups);
  if (isAnyFeatureExist(data)) {
    const featureColumns = getFeatureExportColumn(data, prodLeaderboardFeatureProductFilters);
    return [...dataColumn, ...featureColumns];
  } else {
    return dataColumn;
  }
};
//Function to return column headers for top-selling products export. The ordering is based on given AC, do not simply change it.
export const getDataExportColumn = (currency: string, hasGroups: string): TableColumn[] => {
  return [
    { header: 'Model' },
    ...(hasGroups ? [{ header: 'Grouping' }] : []),
    { header: 'Brand' },
    { header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketRank]} (Revenue)` },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketRank]} Change (Revenue)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketRevenue]} (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketRevenue]} Change (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketRevenue]} Change (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailRevenue]} (${currency})`,
    },
    {
      header: `${
        ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailRevenue]
      } Change (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailRevenue]} Change (%)`,
    },
    { header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerRank]} (Revenue)` },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerRank]} Change (Revenue)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerRevenue]} (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerRevenue]} Change (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerRevenue]} Change (%)`,
    },
    { header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketRank]} (Units)` },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketRank]} Change (Units)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketVolume]}`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketVolume]} Change`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketVolume]} Change (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailVolume]}`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailVolume]} Change`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailVolume]} Change (%)`,
    },
    { header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerRank]} (Units)` },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerRank]} Change (Units)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerVolume]}`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerVolume]} Change`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerVolume]} Change (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketShareRevenue]} (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketShareRevenue]} Change (pp)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerShareRevenue]} (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerShareRevenue]} Change (pp)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketShareVolume]} (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketShareVolume]} Change (pp)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerShareVolume]} (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerShareVolume]} Change (pp)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketPrice]} (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketPrice]} Change (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketPrice]} Change(%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailPrice]} (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailPrice]} Change (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.marketExcludeRetailPrice]} Change(%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerPrice]} (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerPrice]} Change (${currency})`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerPrice]} Change(%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerMixRevenue]} (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerMixRevenue]} Change (pp)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerMixVolume]} (%)`,
    },
    {
      header: `${ColumnHeaderDisplayNameProducts[ColumnHeaderProducts.retailerMixVolume]} Change (pp)`,
    },
  ];
};
export const getFeatureExportColumn = (
  leaderboardExports: LeaderboardKpi[],
  prodLeaderboardFeatureProductFilters: Filters[],
): { header: string }[] => {
  // Only return empty array if none of the items has any features
  if (!leaderboardExports.some((item) => (item?.features?.length ?? 0) > 0)) {
    return [];
  }
  // Product features filters - the order of the column name is based on the productsResponseConfig.response[5].subGroupFilters[0].filters value defined in productAnalyzer
  const featureList: string[] = prodLeaderboardFeatureProductFilters.flatMap((content) => content.filterTitle);
  const headers = featureList.map((header) => ({ header }));
  const firstActivity = [{ header: 'First Activity' }];
  const itemId = [{ header: 'Item Id' }];
  return [...firstActivity, ...itemId, ...headers];
};

export const getFeatureRow = (features: Feature[], prodLeaderboardFeatureProductFilters: Filters[]): DataCell[] => {
  //to sort column cell based on productsResponseConfig.response[5].subGroupFilters[0].filters order value defined in productAnalyzer
  const featureList: string[] = prodLeaderboardFeatureProductFilters.flatMap((content) => content.filterTitle);
  return featureList.map((fixColumn) => {
    const columnCell = features.find((value) => value.name === fixColumn);
    return !columnCell ? { value: '', type: ColumnType.Text } : { value: columnCell.value, type: ColumnType.Text };
  });
};

//Function to return row values for Top Selling table, bar chart export. The ordering is based on the header, do not simply change it.
export const getTopSellingExportRow = (
  data: LeaderboardKpi[],
  prodLeaderboardFeatureProductFilters: Filters[],
  hasGroups,
): DataCell[][] => {
  return data.map((kpiData) => {
    const dataRow = [
      { value: kpiData.item.itemName, type: ColumnType.Text },
      ...(hasGroups ? [{ value: kpiData.grouping, type: ColumnType.Text }] : []),
      { value: kpiData.item.itemBrand, type: ColumnType.Text },
      getFormattedRankingColumn(kpiData.marketRevenueRank?.rank),
      getFormattedRankingChangeColumn(kpiData.marketRevenueRank?.change, kpiData.marketRevenueRank?.position),
      { value: kpiData.marketRevenue?.value, type: ColumnType.Currency },
      { value: kpiData.marketRevenue?.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketRevenue?.percentage),
      { value: kpiData.marketExcludeRetailRevenue?.value, type: ColumnType.Currency },
      { value: kpiData.marketExcludeRetailRevenue?.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketExcludeRetailRevenue?.percentage),
      getFormattedRankingColumn(kpiData.retailerRevenueRank?.rank),
      getFormattedRankingChangeColumn(kpiData.retailerRevenueRank?.change, kpiData.retailerRevenueRank?.position),
      { value: kpiData.retailerRevenue?.value, type: ColumnType.Currency },
      { value: kpiData.retailerRevenue?.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.retailerRevenue?.percentage),
      getFormattedRankingColumn(kpiData.marketVolumeRank?.rank),
      getFormattedRankingChangeColumn(kpiData.marketVolumeRank?.change, kpiData.marketVolumeRank?.position),
      { value: kpiData.marketVolume?.value, type: ColumnType.Number },
      { value: kpiData.marketVolume?.actual, type: ColumnType.Number },
      getFormattedPercentageColumn(kpiData.marketVolume?.percentage),
      { value: kpiData.marketExcludeRetailVolume?.value, type: ColumnType.Number },
      { value: kpiData.marketExcludeRetailVolume?.actual, type: ColumnType.Number },
      getFormattedPercentageColumn(kpiData.marketExcludeRetailVolume?.percentage),
      getFormattedRankingColumn(kpiData.retailerVolumeRank?.rank),
      getFormattedRankingChangeColumn(kpiData.retailerVolumeRank?.change, kpiData.retailerVolumeRank?.position),
      { value: kpiData.retailerVolume?.value, type: ColumnType.Number },
      { value: kpiData.retailerVolume?.actual, type: ColumnType.Number },
      getFormattedPercentageColumn(kpiData.retailerVolume?.percentage),
      getFormattedPercentageColumn(kpiData.marketShareRevenue?.sharePercentage),
      getFormattedPercentagePointColumn(kpiData.marketShareRevenue?.growthPercentage),
      getFormattedPercentageColumn(kpiData.retailerShareRevenue?.sharePercentage),
      getFormattedPercentagePointColumn(kpiData.retailerShareRevenue?.growthPercentage),
      getFormattedPercentageColumn(kpiData.marketShareVolume?.sharePercentage),
      getFormattedPercentagePointColumn(kpiData.marketShareVolume?.growthPercentage),
      getFormattedPercentageColumn(kpiData.retailerShareVolume?.sharePercentage),
      getFormattedPercentagePointColumn(kpiData.retailerShareVolume?.growthPercentage),
      { value: kpiData.marketPrice?.value, type: ColumnType.Currency },
      { value: kpiData.marketPrice?.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketPrice?.percentage),
      { value: kpiData.marketExcludeRetailPrice?.value, type: ColumnType.Currency },
      { value: kpiData.marketExcludeRetailPrice?.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.marketExcludeRetailPrice?.percentage),
      { value: kpiData.retailerPrice?.value, type: ColumnType.Currency },
      { value: kpiData.retailerPrice?.actual, type: ColumnType.Currency },
      getFormattedPercentageColumn(kpiData.retailerPrice?.percentage),
      getFormattedPercentageColumn(kpiData.retailerMixRevenue?.sharePercentage),
      getFormattedPercentagePointColumn(kpiData.retailerMixRevenue?.growthPercentage),
      getFormattedPercentageColumn(kpiData.retailerMixVolume?.sharePercentage),
      getFormattedPercentagePointColumn(kpiData.retailerMixVolume?.growthPercentage),
    ];
    if (isAnyFeatureExist(data)) {
      let featureRow;
      const formattedFirstActivityDate: string = kpiData.item.firstActivityDate
        ? formatFirstActivity(kpiData.item.firstActivityDate)
        : '';
      const formattedItemId: string = kpiData.item.itemId ? kpiData.item.itemId : '';
      const firstActivity = [{ value: formattedFirstActivityDate, type: ColumnType.Text }];
      const itemId = [{ value: formattedItemId, type: ColumnType.Text }];
      if (kpiData.features !== undefined && kpiData.features.length > 0) {
        featureRow = getFeatureRow(kpiData.features, prodLeaderboardFeatureProductFilters);
      } else {
        featureRow = [{ value: FEATURE_UNAVAILABLE, type: ColumnType.Text }];
      }
      return [...dataRow, ...firstActivity, ...itemId, ...featureRow];
    }
    return dataRow;
  });
};

//This function takes rank & position value as param & returns formatted string as column value & ColumnType Number
export const getFormattedRankingChangeColumn = (rank: number, position: string, itemName?: string) => {
  if (itemName && itemName === 'TOTAL') {
    return { value: '', type: ColumnType.Text };
  }
  // Default to NOT_AVAILABLE
  let rankingValue = '--';

  if (position === 'UP') {
    rankingValue = `+${rank}`;
  }
  if (position === 'DOWN') {
    rankingValue = `-${rank}`;
  }
  return { value: rankingValue, type: ColumnType.Text };
};

//This function takes retail ranking value as param & returns blank if rank = 0, else return absolute rank value
export const getFormattedRankingColumn = (rank: number) => {
  const retailRanking: string | number = rank === 0 ? ' ' : rank;
  return { value: retailRanking, type: ColumnType.Number };
};

//Function to return column headers for products compare export. The header column will consist KPI and followed by 13 periods
export const getProductsCompareExportColumn = (productsCompareExports: ProductsCompareExport[]): TableColumn[] => {
  // get first itemContent period list, since all 13 period for each of the export contents are the same 13 periods e.g. Feb 2022 until Feb 2023
  const periodList: string[] = productsCompareExports[0].itemContents.flatMap((content) => content.period);

  const headers: TableColumn[] = periodList.map((header) => ({ header }));

  return [{ header: 'Model' }, { header: 'Brand' }, { header: 'KPI' }, ...headers];
};

export const getTradingAssistantExportColumn = (
  currencySymbol: string,
  retailClientName: string = '',
  isDeepDiveView: boolean = false,
  segmentBy: string = '',
): TableColumn[] => {
  const headers = [
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brand]}` },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerShareForBrandRevenue]} (%)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerShareForBrandRevenue]} (+/-pp)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandShareForRetailerRevenue]} (%)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandShareForRetailerRevenue]} (+/-pp)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandRevenue]} (${currencySymbol})` },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandRevenueChange]} (${currencySymbol})` },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandRevenueChange]} (%)` },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandRevenueShare]} (%)` },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandRevenueShareChange]} (+/-pp)` },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerRevenue]} (${currencySymbol})`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerRevenueChange]} (${currencySymbol})`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerRevenueChange]} (%)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerRevenueShare]} (%)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerRevenueShare]} (+/-pp)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerShareForBrandUnits]} (%)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerShareForBrandUnits]} (+/-pp)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandShareForRetailerUnits]} (%)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandShareForRetailerUnits]} (+/-pp)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandUnits]}` },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandUnitsChange]}` },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandUnitsChange]} (%)` },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandUnitsShare]} (%)` },
    { header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.brandUnitsShareChange]} (+/-pp)` },

    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerUnits]}`.replace('{retailer}', retailClientName),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerUnitsChange]}`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerUnitsChange]} (%)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerUnitsShare]} (%)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
    {
      header: `${ColumnHeaderDisplayNameTA[ColumnHeaderTA.retailerUnitsShareChange]} (+/-pp)`.replace(
        '{retailer}',
        retailClientName,
      ),
    },
  ];
  if (isDeepDiveView) {
    headers.splice(1, 0, { header: segmentBy });
  }
  return headers;
};

type ModelBrandAndKpiColumnHeaderParamFuncType = (
  model: string,
  brand: string,
) => (columnHeaderProducts: ColumnHeaderProducts, symbol?: string) => DataCell[];

/**
 * returns a function to form Column Header with Model, Brand and KPI values
 * @param model Model column
 * @param brand Brand column
 */
const getCompareProductsModelBrandAndKpiColumnHeader: ModelBrandAndKpiColumnHeaderParamFuncType =
  (model, brand) =>
  (columnHeaderProducts, symbol = '') => [
    {
      value: model,
      type: ColumnType.Text,
    },
    {
      value: brand,
      type: ColumnType.Text,
    },
    {
      value: `${ColumnHeaderDisplayNameProducts[columnHeaderProducts]} ${symbol}`,
      type: ColumnType.Text,
    },
  ];

//Function to return row values for Top Selling table, bar chart export. The ordering is based on the header, do not simply change it.
export const getProductsCompareExportRow = (data: ProductsCompareExport[], currency: string): DataCell[][] => {
  let outputData: DataCell[][] = [];
  data.forEach((productsCompareExportData) => {
    const getCompareProductsKpiColumnHeader = getCompareProductsModelBrandAndKpiColumnHeader(
      productsCompareExportData.item.itemName,
      productsCompareExportData.item.itemBrand,
    );

    // Market Revenue Rank row
    const marketRevenueRankPeriodValues: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedRankingColumn(content.marketRevenueRank?.rank),
    );

    const marketRevenueRank: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketRevenueRank),
      ...marketRevenueRankPeriodValues,
    ];

    //Market Revenue Rank Change Row
    const marketRevenueRankChangePeriodValues: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedRankingChangeColumn(
        content.marketRevenueRank?.change,
        content.marketRevenueRank?.position,
        productsCompareExportData.item.itemName,
      ),
    );
    const marketRevenueRankChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketRevenueRankChange),
      ...marketRevenueRankChangePeriodValues,
    ];

    // Market Revenue Row
    const marketRevenuePeriodValues: DataCell[] = productsCompareExportData.itemContents.map((content) => ({
      value: content.marketRevenue.value,
      type: ColumnType.Currency,
    }));
    const marketRevenue: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketRevenue, `(${currency})`),
      ...marketRevenuePeriodValues,
    ];

    // Market Revenue Change Row
    const marketRevenueChangePeriodValues: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.marketRevenue.percentage),
    );
    const marketRevenueChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketRevenueChange, Symbol.PERCENTAGE),
      ...marketRevenueChangePeriodValues,
    ];

    // Market Revenue Share row
    const marketRevenueSharePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.marketShareRevenue.sharePercentage),
    );
    const marketRevenueShare: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketShareRevenue, Symbol.PERCENTAGE),
      ...marketRevenueSharePeriodValue,
    ];

    // Market Revenue Share Percentage Point row
    const marketRevenueSharePercentagePointPeriodValue: DataCell[] = productsCompareExportData.itemContents.map(
      (content) => ({
        value: content.marketShareRevenue?.growthPercentage,
        type: ColumnType.PercentagePoint,
      }),
    );
    const marketRevenueSharePercentagePoint: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketShareRevenue, Symbol.PERCENTAGE_POINT),
      ...marketRevenueSharePercentagePointPeriodValue,
    ];

    // Market Volume rank row
    const marketVolumeRankPeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedRankingColumn(content.marketVolumeRank?.rank),
    );
    const marketVolumeRank: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketVolumeRank),
      ...marketVolumeRankPeriodValue,
    ];

    //Market Volume Rank Change Row
    const marketVolumeRankChangePeriodValues: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedRankingChangeColumn(
        content.marketVolumeRank?.change,
        content.marketVolumeRank?.position,
        productsCompareExportData.item.itemName,
      ),
    );
    const marketVolumeRankChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketVolumeRankChange),
      ...marketVolumeRankChangePeriodValues,
    ];

    //Market Volume row
    const marketVolumePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) => ({
      value: content.marketVolume.value,
      type: ColumnType.Number,
    }));
    const marketVolume: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketVolume),
      ...marketVolumePeriodValue,
    ];

    // Market Volume Change row
    const marketVolumeChangePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.marketVolume.percentage),
    );
    const marketVolumeChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketVolumeChange, Symbol.PERCENTAGE),
      ...marketVolumeChangePeriodValue,
    ];

    // Market Volume Share row
    const marketVolumeSharePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.marketShareVolume.sharePercentage),
    );
    const marketVolumeShare: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketShareVolume, Symbol.PERCENTAGE),
      ...marketVolumeSharePeriodValue,
    ];

    // Market Volume Share Percentage Point row
    const marketVolumeSharePercentagePointPeriodValue: DataCell[] = productsCompareExportData.itemContents.map(
      (content) => ({
        value: content.marketShareVolume?.growthPercentage,
        type: ColumnType.PercentagePoint,
      }),
    );
    const marketVolumeSharePercentagePoint: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketShareVolume, Symbol.PERCENTAGE_POINT),
      ...marketVolumeSharePercentagePointPeriodValue,
    ];

    // Market Price row
    const marketPricePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) => ({
      value: content.marketPrice.value,
      type: ColumnType.Currency,
    }));
    const marketPrice: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketPrice, `(${currency})`),
      ...marketPricePeriodValue,
    ];

    // Market Price Change row
    const marketPriceChangePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.marketPrice.percentage),
    );
    const marketPriceChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketPriceChange, Symbol.PERCENTAGE),
      ...marketPriceChangePeriodValue,
    ];

    // Retailer Revenue Ranking row
    const retailerRevenueRankingPeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedRankingColumn(content.retailerRevenueRank?.rank),
    );
    const retailerRevenueRanking: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerRevenueRank),
      ...retailerRevenueRankingPeriodValue,
    ];

    // Retailer Revenue Ranking Change row
    const retailerRevenueRankingChangePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedRankingChangeColumn(
        content.retailerRevenueRank?.change,
        content.retailerRevenueRank?.position,
        productsCompareExportData.item.itemName,
      ),
    );
    const retailerRevenueRankingChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerRevenueRankChange),
      ...retailerRevenueRankingChangePeriodValue,
    ];

    // Retailer Revenue row
    const retailerRevenuePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) => ({
      value: content.retailerRevenue.value,
      type: ColumnType.Currency,
    }));
    const retailerRevenue: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerRevenue, `(${currency})`),
      ...retailerRevenuePeriodValue,
    ];

    // Retailer Revenue Change row
    const retailerRevenueChangePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerRevenue.percentage),
    );
    const retailerRevenueChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerRevenueChange, Symbol.PERCENTAGE),
      ...retailerRevenueChangePeriodValue,
    ];

    // Retailer Revenue Share row
    const retailerRevenueSharePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerShareRevenue.sharePercentage),
    );
    const retailerRevenueShare: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerShareRevenue, Symbol.PERCENTAGE),
      ...retailerRevenueSharePeriodValue,
    ];

    // Retailer Revenue Share Percentage Point row
    const retailerRevenueSharePercentagePointPeriodValue: DataCell[] = productsCompareExportData.itemContents.map(
      (content) => ({
        value: content.retailerShareRevenue.growthPercentage,
        type: ColumnType.PercentagePoint,
      }),
    );
    const retailerRevenueSharePercentagePoint: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerShareRevenue, Symbol.PERCENTAGE_POINT),
      ...retailerRevenueSharePercentagePointPeriodValue,
    ];

    // Retailer Revenue Mix row
    const retailerRevenueMixPeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerMixRevenue.sharePercentage),
    );
    const retailerRevenueMix: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerMixRevenue, Symbol.PERCENTAGE),
      ...retailerRevenueMixPeriodValue,
    ];

    // Retailer Revenue Mix Percentage Point row
    const retailerRevenueMixPercentagePointPeriodValue: DataCell[] = productsCompareExportData.itemContents.map(
      (content) => ({
        value: content.retailerMixRevenue?.growthPercentage,
        type: ColumnType.PercentagePoint,
      }),
    );
    const retailerRevenueMixPercentagePoint: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerMixRevenue, Symbol.PERCENTAGE_POINT),
      ...retailerRevenueMixPercentagePointPeriodValue,
    ];

    // Retailer Volume Rank row
    const retailerVolumeRankPeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedRankingColumn(content.retailerVolumeRank?.rank),
    );
    const retailerVolumeRank: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerVolumeRank),
      ...retailerVolumeRankPeriodValue,
    ];

    // Retailer Revenue Ranking Change row
    const retailerVolumeRankingChangePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedRankingChangeColumn(
        content.retailerVolumeRank?.change,
        content.retailerVolumeRank?.position,
        productsCompareExportData.item.itemName,
      ),
    );
    const retailerVolumeRankingChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerVolumeRankChange),
      ...retailerVolumeRankingChangePeriodValue,
    ];

    // Retailer Volume row
    const retailerVolumePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) => ({
      value: content.retailerVolume.value,
      type: ColumnType.Number,
    }));
    const retailerVolume: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerVolume),
      ...retailerVolumePeriodValue,
    ];

    // Retailer Volume Change row
    const retailerVolumeChangePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerVolume.percentage),
    );
    const retailerVolumeChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerVolumeChange, Symbol.PERCENTAGE),
      ...retailerVolumeChangePeriodValue,
    ];

    // Retailer Volume Share row
    const retailerVolumeSharePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerShareVolume.sharePercentage),
    );
    const retailerVolumeShare: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerShareVolume, Symbol.PERCENTAGE),
      ...retailerVolumeSharePeriodValue,
    ];

    // Retailer Volume Share Percentage Point row
    const retailerVolumeSharePercentagePointPeriodValue: DataCell[] = productsCompareExportData.itemContents.map(
      (content) => ({
        value: content.retailerShareVolume.growthPercentage,
        type: ColumnType.PercentagePoint,
      }),
    );
    const retailerVolumeSharePercentagePoint: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerShareVolume, Symbol.PERCENTAGE_POINT),
      ...retailerVolumeSharePercentagePointPeriodValue,
    ];

    // Retailer Volume Mix row
    const retailerVolumeMixPeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerMixVolume.sharePercentage),
    );
    const retailerVolumeMix: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerMixVolume, Symbol.PERCENTAGE),
      ...retailerVolumeMixPeriodValue,
    ];

    // Retailer Volume Mix Percentage Point row
    const retailerVolumeMixPercentagePointPeriodValue: DataCell[] = productsCompareExportData.itemContents.map(
      (content) => ({
        value: content.retailerMixVolume?.growthPercentage,
        type: ColumnType.PercentagePoint,
      }),
    );
    const retailerVolumeMixPercentagePoint: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerMixVolume, Symbol.PERCENTAGE_POINT),
      ...retailerVolumeMixPercentagePointPeriodValue,
    ];

    // Retailer Price row
    const retailerPricePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) => ({
      value: content.retailerPrice.value,
      type: ColumnType.Currency,
    }));
    const retailerPrice: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerPrice, `(${currency})`),
      ...retailerPricePeriodValue,
    ];

    // Retailer Price Change row
    const retailerPriceChangePeriodValue: DataCell[] = productsCompareExportData.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerPrice.percentage),
    );
    const retailerPriceChange: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerPriceChange, Symbol.PERCENTAGE),
      ...retailerPriceChangePeriodValue,
    ];

    outputData.push(
      marketRevenueRank,
      marketRevenueRankChange,
      marketRevenue,
      marketRevenueChange,
      marketRevenueShare,
      marketRevenueSharePercentagePoint,
      marketVolumeRank,
      marketVolumeRankChange,
      marketVolume,
      marketVolumeChange,
      marketVolumeShare,
      marketVolumeSharePercentagePoint,
      marketPrice,
      marketPriceChange,
      retailerRevenueRanking,
      retailerRevenueRankingChange,
      retailerRevenue,
      retailerRevenueChange,
      retailerRevenueShare,
      retailerRevenueSharePercentagePoint,
      retailerRevenueMix,
      retailerRevenueMixPercentagePoint,
      retailerVolumeRank,
      retailerVolumeRankingChange,
      retailerVolume,
      retailerVolumeChange,
      retailerVolumeShare,
      retailerVolumeSharePercentagePoint,
      retailerVolumeMix,
      retailerVolumeMixPercentagePoint,
      retailerPrice,
      retailerPriceChange,
    );
  });
  return outputData;
};
